import {
    SeaEmissionsAggregatedByCountryLaneAndCarrier,
    SeaEmissionsByCountryLane,
} from '@lune-fe/dapi-models'
import { formatNumbers, Select, Text } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import _ from 'lodash'
import { useCallback, useEffect, useState } from 'react'

import { CountryLaneAndCarrierChart } from 'views/ReduceEmissions/CountryLaneAndCarrierChart'
import { SortButton } from 'views/ReduceEmissions/SortButton'

enum SortByOption {
    TEUS_ASC = 'TEUS_ASC',
    TEUS_DESC = 'TEUS_DESC',
    TOTAL_EMISSIONS_ASC = 'TOTAL_EMISSIONS_ASC',
    TOTAL_EMISSIONS_DESC = 'TOTAL_EMISSIONS_DESC',
    CARBON_INTENSITY_ASC = 'CARBON_INTENSITY_ASC',
    CARBON_INTENSITY_DESC = 'CARBON_INTENSITY_DESC',
}

function sortAggregatedCarriers(
    data: SeaEmissionsByCountryLane,
    sortByKey: 'totalEmissions' | 'totalTeu' | 'carbonIntensity',
    ascending: boolean,
): SeaEmissionsByCountryLane {
    return {
        ...data,
        emissionsByCountryLane: data.emissionsByCountryLane.sort((a, b) => {
            const compareValue = a[sortByKey] - b[sortByKey]
            return ascending ? compareValue : -compareValue
        }),
    }
}

export const CountryLaneAndCarrierMetrics = ({
    data,
    preselectedCarrier,
    onSelectChange,
}: {
    data: SeaEmissionsAggregatedByCountryLaneAndCarrier
    preselectedCarrier?: string
    onSelectChange: () => void
}) => {
    const { aggregatedCarriers, topCarriers } = data
    const [selectedFilterIndex, setSelectedFilterIndex] = useState<number | null>(null)
    const [activeSortOption, setActiveSortOption] = useState<SortByOption>(
        SortByOption.TOTAL_EMISSIONS_DESC,
    )

    const getSortedData = useCallback(() => {
        const data =
            selectedFilterIndex === null
                ? aggregatedCarriers
                : topCarriers[selectedFilterIndex - 1].data
        switch (activeSortOption) {
            case SortByOption.TOTAL_EMISSIONS_ASC:
                return sortAggregatedCarriers(data, 'totalEmissions', true)
            case SortByOption.TOTAL_EMISSIONS_DESC:
                return sortAggregatedCarriers(data, 'totalEmissions', false)
            case SortByOption.TEUS_ASC:
                return sortAggregatedCarriers(data, 'totalTeu', true)
            case SortByOption.TEUS_DESC:
                return sortAggregatedCarriers(data, 'totalTeu', false)
            case SortByOption.CARBON_INTENSITY_ASC:
                return sortAggregatedCarriers(data, 'carbonIntensity', true)
            case SortByOption.CARBON_INTENSITY_DESC:
                return sortAggregatedCarriers(data, 'carbonIntensity', false)
            default:
                return data
        }
    }, [activeSortOption, aggregatedCarriers, selectedFilterIndex])

    const getMaxValues = useCallback(() => {
        const data =
            selectedFilterIndex === null
                ? aggregatedCarriers.emissionsByCountryLane
                : topCarriers[selectedFilterIndex - 1].data.emissionsByCountryLane
        const maxCarrierIntensity = _.maxBy(data, 'carbonIntensity')
        const maxTotalEmissions = _.maxBy(data, 'totalEmissions')
        const maxAverage = _.maxBy(data, (d) => d.tradeLaneAverage?.average || 0)

        return {
            maxCarbonIntensity: maxCarrierIntensity?.carbonIntensity || 0,
            maxTotalEmissions: maxTotalEmissions?.totalEmissions || 0,
            maxAverage: maxAverage?.tradeLaneAverage?.average || 0,
        }
    }, [selectedFilterIndex])

    useEffect(() => {
        if (preselectedCarrier) {
            const carrier = topCarriers.find((c) => c.carrier === preselectedCarrier)
            if (carrier) {
                setSelectedFilterIndex(topCarriers.indexOf(carrier) + 1)
            }
        }
    }, [preselectedCarrier])

    return (
        <Box
            sx={{
                overflow: 'auto',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    mb: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Text
                    variant={'h5'}
                    sx={{
                        width: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    Top Country Pairs
                </Text>
                <Select
                    sx={{ width: '220px' }}
                    label={'Filter by Carriers'}
                    items={[
                        ...[{ label: 'No filter', value: null }],
                        ...topCarriers.map((carrier, index) => ({
                            label: carrier.carrier,
                            value: index + 1,
                        })),
                    ]}
                    value={selectedFilterIndex}
                    onChange={(selected) => {
                        setSelectedFilterIndex(selected)
                        onSelectChange()
                    }}
                />
            </Box>
            <Box>
                <Box
                    sx={{
                        minWidth: '800px',
                        width: '100%',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 3,
                            width: '100%',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            '.MuiTypography-root': {
                                lineHeight: '24px',
                                '&:nth-child(1)': {
                                    width: '10%',
                                },
                                '&:nth-child(2)': {
                                    width: '16%',
                                },
                                '&:nth-child(3)': {
                                    width: '37%',
                                },
                                '&:nth-child(4)': {
                                    width: '37%',
                                },
                            },
                        }}
                    >
                        <Text variant={'button'}>
                            <SortButton
                                options={[SortByOption.TEUS_DESC, SortByOption.TEUS_ASC]}
                                active={activeSortOption}
                                onClick={(newVal) => setActiveSortOption(newVal as SortByOption)}
                            />
                            TEUs
                        </Text>
                        <Text variant={'button'}>Country lane</Text>
                        <Text variant={'button'}>
                            <SortButton
                                options={[
                                    SortByOption.TOTAL_EMISSIONS_DESC,
                                    SortByOption.TOTAL_EMISSIONS_ASC,
                                ]}
                                active={activeSortOption}
                                onClick={(newVal) => setActiveSortOption(newVal as SortByOption)}
                            />
                            Total emissions tCO₂e
                        </Text>
                        <Text variant={'button'}>
                            <SortButton
                                options={[
                                    SortByOption.CARBON_INTENSITY_DESC,
                                    SortByOption.CARBON_INTENSITY_ASC,
                                ]}
                                active={activeSortOption}
                                onClick={(newVal) => setActiveSortOption(newVal as SortByOption)}
                            />
                            Country lane intensity
                        </Text>
                    </Box>
                    <CountryLaneAndCarrierChart
                        data={getSortedData()}
                        highestCarbonIntensity={getMaxValues().maxCarbonIntensity}
                        highestTotalEmissions={getMaxValues().maxTotalEmissions}
                        maxAverage={getMaxValues().maxAverage}
                    />
                </Box>
                <Text
                    variant={'body3'}
                    sx={{
                        textAlign: 'right',
                        width: '100%',
                        display: 'block',
                        mt: 2,
                    }}
                >
                    Analysed <strong>{formatNumbers(getSortedData().totalLegs)}</strong> shipments
                </Text>
            </Box>
        </Box>
    )
}
